import { z } from 'zod';
import { uuidSchema } from './common.schema';

export const InventoryMovementSummarySchema = z.object({
  movement_id: uuidSchema,
  container_id: uuidSchema,
  container_identifier: z.string().nullable(),
  from_location_name: z.string().nullable(),
  to_location_name: z.string(),
  moved_by: uuidSchema,
  moved_by_name: z.string().nullable(),
  moved_at: z.string(),
  reason: z.string().nullable(),
  new_expiration_date: z.string().nullable(),
});

export type InventoryMovementSummary = z.infer<typeof InventoryMovementSummarySchema>;

export const InventoryMovementDetailSchema = z.object({
  movement_id: uuidSchema,
  container_id: uuidSchema,
  container_identifier: z.string().nullable(),
  hospital_id: uuidSchema,
  from_location_id: uuidSchema.nullable(),
  to_location_id: uuidSchema.nullable(),
  from_location_name: z.string().nullable(),
  to_location_name: z.string(),
  container_type: z.string().nullable(),
  moved_by: uuidSchema,
  moved_by_name: z.string().nullable(),
  reason: z.string().nullable(),
  old_expiration_date: z.string().nullable(),
  new_expiration_date: z.string().nullable(),
  status: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type InventoryMovementDetail = z.infer<typeof InventoryMovementDetailSchema>;

export const ListInventoryMovementsQuerySchema = z.object({
  hospital_id: uuidSchema,
  from_date: z.preprocess(
    (v) => (v === '' || v === null ? undefined : v),
    z.string().datetime({ offset: true }).optional()
  ),
  to_date: z.preprocess(
    (v) => (v === '' || v === null ? undefined : v),
    z.string().datetime({ offset: true }).optional()
  ),
  location: z.preprocess(
    (v) => (v === '' || v === null ? undefined : v),
    z.string().optional()
  ),
  container_identifier: z.preprocess(
    (v) => (v === '' || v === null ? undefined : v),
    z.string().optional()
  ),
  // Bounds the page size so a single request (and the per-row Cognito `moved_by`
  // name-resolution fan-out in inventoryMovements.service.ts) can never balloon into
  // an unbounded burst of ListUsers calls as movement history grows.
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
});

export type ListInventoryMovementsQuery = z.infer<typeof ListInventoryMovementsQuerySchema>;

// ---------------------------------------------------------------------------
// Search (pre-movement item lookup)
// ---------------------------------------------------------------------------

/**
 * Entity types used in search results and bulk-move items.
 * Values deliberately mirror MOVEMENT_SEARCH_TYPES so the client knows exactly
 * which table each item belongs to.
 *   receivable / mother_donor_mrn → milk_receivables
 *   batch_bottle_id               → milk_inventory
 *   patient_mrn / sec_code / feed_split → feed_prep_splits
 *   lot_unit_id                   → inventory_units
 */
export const MOVEMENT_ENTITY_TYPES = [
  'receivable',       // milk_receivables — found by receipt_number
  'batch_bottle_id',  // milk_inventory   — found by container_barcode
  'patient_mrn',      // feed_prep_splits — found by patient MRN
  'mother_donor_mrn', // milk_receivables — mother + donor receivable
  'sec_code',         // feed_prep_splits — found by SEC code
  'feed_split',       // feed_prep_splits — found by syringe barcode / location
  'lot_unit_id',      // inventory_units  — found by tms_uuid
] as const;

export type MovementEntityType = (typeof MOVEMENT_ENTITY_TYPES)[number];

/**
 * Search-type enum — tells the API exactly which search path to use.
 * Required when `q` is provided; ignored for `location` searches.
 */
export const MOVEMENT_SEARCH_TYPES = [
  'receivable',      // milk_receivables.receipt_number
  'batch_bottle_id', // milk_inventory.container_barcode or batch_number
  'patient_mrn',     // feed_prep_splits for that patient MRN
  'mother_donor_mrn',// mother: received bottles; donor: bottles + batch bottles
  'sec_code',        // sec_traceability.canonical_id / sec_human_readable → split
  'feed_split',      // feed_prep_splits.syringe_label_barcode
  'lot_unit_id',     // inventory_units.tms_uuid (product barcode)
] as const;

export type MovementSearchType = (typeof MOVEMENT_SEARCH_TYPES)[number];

export const MovementSearchResultSchema = z.object({
  entity_type: z.enum(MOVEMENT_ENTITY_TYPES),
  entity_id: uuidSchema,
  /** Human-readable primary identifier (receipt_number, batch_number, barcode, MRN, etc.) */
  label: z.string(),
  container_identifier: z.string().nullable(),
  /** Values: mother_bottle | donor_bottle | batch | feed | product */
  container_type: z.string().nullable(),
  storage_location_id: uuidSchema.nullable(),
  storage_location_name: z.string().nullable(),
  expiry_at: z.string().nullable(),
  /** ISO timestamp of when the milk was pumped/collected. Only present for MILK_BATCH results. */
  pump_date: z.string().nullable(),
  status: z.string(),
  /** Which search path produced this result */
  matched_by: z.string(),
  /** Volume in ml (or quantity + unit for products). Present for all entity types. */
  volume_ml: z.number().nullable(),
  /** Milk type (e.g. FRESH, FROZEN). Present for COLLECTION, MILK_BOTTLE, MILK_BATCH. */
  milk_type: z.string().nullable(),
  /** Full name of associated person: mother/donor (provider) or patient. */
  person_name: z.string().nullable(),
  /** Product form (e.g. Powder, Liquid). Only for PRODUCT_UNIT. */
  form_factor: z.string().nullable(),
  /** Product brand name. Only for PRODUCT_UNIT. */
  brand_name: z.string().nullable(),
  /** Milk safety/processing status (e.g. FRESH, FROZEN, PASTEURIZED). Present for COLLECTION and MILK_BOTTLE. */
  milk_status: z.string().nullable(),
});

export type MovementSearchResult = z.infer<typeof MovementSearchResultSchema>;

export const MovementSearchQuerySchema = z.object({
  hospital_id: uuidSchema,
  q: z.preprocess(
    (v) => (v === '' || v === null ? undefined : v),
    z.string().min(1).max(200).optional(),
  ),
  /** Required when `q` is provided. Tells the API which search path to use. */
  search_type: z.preprocess(
    (v) => (v === '' || v === null ? undefined : v),
    z.enum(MOVEMENT_SEARCH_TYPES).optional(),
  ),
  /** UUID of the selected location (storage_location_id or inventory_location_id). Used for exact ID-based filtering. */
  location: z.preprocess(
    (v) => (v === '' || v === null ? undefined : v),
    uuidSchema.optional(),
  ),
}).refine(
  (data) => data.q !== undefined || data.location !== undefined,
  { message: 'Either q or location must be provided', path: ['q'] },
).refine(
  (data) => data.location !== undefined || data.search_type !== undefined,
  { message: 'search_type is required when q is provided', path: ['search_type'] },
);

export type MovementSearchQuery = z.infer<typeof MovementSearchQuerySchema>;

// ---------------------------------------------------------------------------
// Bulk Move
// ---------------------------------------------------------------------------

export const BulkMoveItemSchema = z.object({
  entity_type: z.enum(MOVEMENT_ENTITY_TYPES),
  entity_id: uuidSchema,
  /** Current storage_location_id from search result. Used for same-location check. */
  current_location_id: uuidSchema.nullable(),
  /** Optional new expiry date for this item. If omitted the entity's existing expiry is preserved. */
  new_expiry_date: z.preprocess(
    (v) => (v === '' || v === null ? undefined : v),
    z.string().datetime({ offset: true }).optional()
  ),
});

export type BulkMoveItem = z.infer<typeof BulkMoveItemSchema>;

export const BulkMoveRequestSchema = z.object({
  hospital_id: uuidSchema,
  to_location_id: uuidSchema,
  reason: z.string().min(1).max(500),
  moved_by: uuidSchema,
  items: z.array(BulkMoveItemSchema).min(1).max(100),
});

export type BulkMoveRequest = z.infer<typeof BulkMoveRequestSchema>;

export const BulkMoveValidationErrorSchema = z.object({
  entity_type: z.enum(MOVEMENT_ENTITY_TYPES),
  entity_id: uuidSchema,
  label: z.string(),
  reason: z.enum(['EXPIRED', 'QUARANTINED', 'SAME_LOCATION', 'NOT_FOUND', 'INVALID_STATUS']),
});

export type BulkMoveValidationError = z.infer<typeof BulkMoveValidationErrorSchema>;

export const BulkMoveResultSchema = z.object({
  movement_ids: z.array(uuidSchema),
  moved_count: z.number().int(),
});

export type BulkMoveResult = z.infer<typeof BulkMoveResultSchema>;
